import { FormControl, VFlow } from 'bold-ui'
import { TextAreaField } from 'components/form'
import {
  ProcedimentoOdontoSelectField,
  ProcedimentoOdontoSelectModel,
} from 'components/form/field/select/ProcedimentoOdontoSelectField'
import createDecorator from 'final-form-calculate'
import { ParteBucalEnum, TipoParteBucalEnum } from 'graphql/types.generated'
import React, { useEffect, useState } from 'react'
import { Form, FormRenderProps } from 'react-final-form'
import { metaPath } from 'util/metaPath'

import OdontogramaPopper, { OdontogramaPopperProps } from '../components/OdontogramaPopper'
import ReplicarItemSelectField, {
  convertParteBucalEnumToReplicarItem,
  ReplicarItemSelectFieldModel,
} from '../components/ReplicarItemSelectField'
import { getParteBucal, getPartesBucalComProcedimento, getSextantes } from '../util'
import { SextanteFieldModel } from './SextantesField'

export interface SextanteFormPopperProps extends Omit<OdontogramaPopperProps, 'title' | 'onClose'> {
  parteBucalEnum: ParteBucalEnum
  initialValues?: FormRenderProps<SextanteFieldModel>['initialValues']
  cidadaoDataNascimento: LocalDate
  sextantesValues: Record<ParteBucalEnum, SextanteFieldModel>
  onSubmit(values: SextanteFieldModel): void
}

export interface SextanteFormModel extends SextanteFieldModel {
  replicarItems?: ReplicarItemSelectFieldModel[]
}

const meta = metaPath<SextanteFormModel>()

const sextanteFormDecorator = createDecorator({
  field: meta.procedimentos.absolutePath(),
  updates: {
    [meta.observacoes.absolutePath()]: (value: ProcedimentoOdontoSelectModel[], allValues: SextanteFormModel) => {
      if (value.length > 0) return allValues.observacoes
    },
    [meta.replicarItems.absolutePath()]: (value: ProcedimentoOdontoSelectModel[], allValues: SextanteFormModel) => {
      if (value.length > 0) return allValues.replicarItems
    },
  },
})

export default function SextanteFormPopper(props: SextanteFormPopperProps) {
  const { parteBucalEnum, cidadaoDataNascimento, initialValues, sextantesValues, onSubmit, ...popperProps } = props
  const [replicarItems, setReplicarItems] = useState<ReplicarItemSelectFieldModel[]>()
  const [itemsDisabled, setItemsDisabled] = useState<ParteBucalEnum[]>()
  const sextante = getParteBucal(parteBucalEnum)

  useEffect(() => {
    setReplicarItems(
      convertParteBucalEnumToReplicarItem(getSextantes()).filter((item) => item.value !== parteBucalEnum)
    )
  }, [parteBucalEnum])

  useEffect(() => {
    sextantesValues && setItemsDisabled(getPartesBucalComProcedimento(sextantesValues))
  }, [sextantesValues])

  const renderForm = (formProps: FormRenderProps<SextanteFieldModel>) => {
    const disabled = !formProps?.values?.procedimentos?.length

    return (
      <OdontogramaPopper title={sextante.title} onClose={formProps.handleSubmit} {...popperProps}>
        <VFlow>
          <ProcedimentoOdontoSelectField
            name={meta.procedimentos}
            label='Procedimentos (Evolução odontológica)'
            multiple
            tipoParteBucal={TipoParteBucalEnum.SEXTANTE}
            dataNascimento={cidadaoDataNascimento}
          />

          <FormControl label='Observações'>
            <TextAreaField
              name={meta.observacoes[0]}
              maxLength={400}
              placeholder='Insira informações adicionais sobre os procedimentos realizados'
              style={{ resize: 'none' }}
              disabled={disabled}
            />
          </FormControl>

          <ReplicarItemSelectField
            name={meta.replicarItems}
            label='Replicar informações para'
            items={replicarItems}
            itemsDisabled={itemsDisabled}
            disabled={disabled}
          />
        </VFlow>
      </OdontogramaPopper>
    )
  }

  return (
    <Form<SextanteFormModel>
      render={renderForm}
      onSubmit={onSubmit}
      initialValues={initialValues}
      decorators={[sextanteFormDecorator]}
    />
  )
}
